import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { Box, TextField, Select, MenuItem } from "@mui/material";
import { useDebounce } from "minimal-shared";
import InventorySearchModal from "./InventorySearchModal";
import { hasSearchableProductQuery } from "../../../../productSearch/matchProductTokens";

const QuickOrderProductSearch = forwardRef(function QuickOrderProductSearch(
  {
    disabled = false,
    selectedCustomer = null,
    productOptions = [],
    isLoading = false,
    onProductSelect,
    onRequireCustomer,
  },
  ref
) {
  const [productSearchInput, setProductSearchInput] = useState("");
  const [productSearchMode, setProductSearchMode] = useState("code");
  const [inventoryModalOpen, setInventoryModalOpen] = useState(false);
  const [inventoryModalAnchorPosition, setInventoryModalAnchorPosition] = useState(null);
  const inputRef = useRef(null);
  const modalOpenRef = useRef(false);
  const debouncedSearchInput = useDebounce(productSearchInput, 120);

  useEffect(() => {
    modalOpenRef.current = inventoryModalOpen;
  }, [inventoryModalOpen]);

  const focusInput = useCallback((immediate = false) => {
    const run = () => inputRef.current?.focus?.();
    if (immediate) {
      run();
      return;
    }
    requestAnimationFrame(run);
  }, []);

  const clearSearch = useCallback(() => {
    setProductSearchInput("");
  }, []);

  const closeInventoryModal = useCallback((options = {}) => {
    if (typeof document !== "undefined") {
      const active = document.activeElement;
      if (active instanceof HTMLElement && active.closest?.(".MuiDialog-root")) {
        active.blur();
      }
    }
    setInventoryModalOpen(false);
    setProductSearchInput("");
    if (!options?.skipFocus) {
      focusInput(true);
    }
  }, [focusInput]);

  const updateAnchorFromInput = useCallback(() => {
    const inputRect = inputRef.current?.getBoundingClientRect?.();
    if (!inputRect) return;
    setInventoryModalAnchorPosition((prev) => {
      const next = {
        top: inputRect.bottom + 8,
        left: inputRect.left,
        width: inputRect.width,
      };
      if (
        prev &&
        prev.top === next.top &&
        prev.left === next.left &&
        prev.width === next.width
      ) {
        return prev;
      }
      return next;
    });
  }, []);

  const openModal = useCallback(() => {
    updateAnchorFromInput();
    setInventoryModalOpen(true);
  }, [updateAnchorFromInput]);

  useImperativeHandle(
    ref,
    () => ({
      focus: focusInput,
      blur: () => inputRef.current?.blur?.(),
      clear: clearSearch,
      closeModal: closeInventoryModal,
      openModal,
      getValue: () => productSearchInput,
      getInputEl: () => inputRef.current,
      getBoundingClientRect: () => inputRef.current?.getBoundingClientRect?.(),
    }),
    [focusInput, clearSearch, closeInventoryModal, openModal, productSearchInput]
  );

  const handleChange = useCallback(
    (event) => {
      const value = event.target.value;
      setProductSearchInput(value);
      if (disabled || !selectedCustomer?.id) {
        return;
      }
      if (hasSearchableProductQuery(value) && !modalOpenRef.current) {
        updateAnchorFromInput();
        setInventoryModalOpen(true);
      }
    },
    [disabled, selectedCustomer?.id, updateAnchorFromInput]
  );

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key !== "Enter") {
        return;
      }
      event.preventDefault();
      if (!selectedCustomer?.id) {
        onRequireCustomer?.();
        focusInput(true);
        return;
      }
      if (hasSearchableProductQuery(productSearchInput)) {
        updateAnchorFromInput();
        setInventoryModalOpen(true);
      } else {
        focusInput(true);
      }
    },
    [selectedCustomer?.id, productSearchInput, onRequireCustomer, focusInput, updateAnchorFromInput]
  );

  const handleCloseModal = useCallback(() => {
    closeInventoryModal({ skipFocus: false });
  }, [closeInventoryModal]);

  const handleProductSelect = useCallback(
    (product) => {
      onProductSelect?.(product);
    },
    [onProductSelect]
  );

  return (
    <>
      <Box className="quick-order-product-search-wrap">
        <TextField
          className="input-border quick-order-product-search"
          placeholder="Enter Product Name or Code"
          inputRef={inputRef}
          value={productSearchInput}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          sx={{ width: "100%", height: "35px", minWidth: 0 }}
          size="small"
          disabled={disabled}
          autoComplete="off"
        />
        <Select
          className="input-border"
          value={productSearchMode}
          onChange={(e) => setProductSearchMode(e.target.value)}
          size="small"
          disabled={disabled}
          sx={{ height: "35px", minWidth: "110px", flex: "0 0 auto" }}
        >
          <MenuItem value="all">All</MenuItem>
          <MenuItem value="code">P. Code</MenuItem>
          <MenuItem value="name">P. Name</MenuItem>
        </Select>
      </Box>
      <InventorySearchModal
        open={inventoryModalOpen}
        onClose={handleCloseModal}
        onProductSelect={handleProductSelect}
        productOptions={productOptions}
        isLoading={isLoading}
        productSearchInput={debouncedSearchInput}
        productSearchMode={productSearchMode}
        selectedCustomer={selectedCustomer}
        anchorPosition={inventoryModalAnchorPosition}
        onFocusOuterProductSearch={() => focusInput(true)}
      />
    </>
  );
});

export default React.memo(QuickOrderProductSearch);
